import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Check, X, BadgePercent, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { setVerifiedDiscount } from "@/lib/veteran";

const INPUT = "w-full bg-navy border border-white/20 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors";

// Quick self-attestation → issues a personal, single-use discount code.
export function VeteranVerifyModal({ percent, onVerified, onClose }) {
  const [f, setF] = useState({ name: "", email: "", branch: "", service_status: "veteran", years: "", attest: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.attest) { toast.error("Please confirm your service to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/veteran/verify", f);
      const d = { code: data.code, percent: data.percent };
      setVerifiedDiscount(d);
      setResult(d);
      onVerified?.(d);
    } catch (err) { toast.error(formatApiError(err) || "Could not verify — please try again."); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(result.code); toast.success("Code copied."); };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="veteran-verify-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-navy-800 border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em]"><ShieldCheck size={15} /> Verify your service</div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close" data-testid="verify-close"><X size={18} /></button>
        </div>

        {result ? (
          <div className="text-center py-4" data-testid="verify-success">
            <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold mx-auto mb-3"><BadgePercent size={24} /></div>
            <h3 className="font-heading text-xl text-white mb-1">You're verified — {result.percent}% off</h3>
            <p className="text-gray-400 text-sm mb-4">Here's your personal discount code. It's tied to you and works once at checkout.</p>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="font-heading text-lg text-gold tracking-wide border border-gold/40 bg-gold/10 px-4 py-2" data-testid="verify-code">{result.code}</span>
              <button onClick={copy} data-testid="verify-copy" className="text-gray-400 hover:text-gold p-2" aria-label="Copy code"><Copy size={16} /></button>
            </div>
            <button onClick={onClose} data-testid="verify-done" className="w-full bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="verify-form">
            <p className="text-sm text-gray-300">A quick thank-you step. Confirm your service and we'll unlock your {percent ? `${percent}%` : ""} discount instantly.</p>
            <div className="grid grid-cols-2 gap-3">
              <input className={INPUT} placeholder="Name (optional)" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="verify-name" />
              <input className={INPUT} placeholder="Email (optional)" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="verify-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={INPUT} placeholder="Branch (e.g. Army)" value={f.branch} onChange={(e) => set("branch", e.target.value)} data-testid="verify-branch" />
              <input className={INPUT} placeholder="Years of service" value={f.years} onChange={(e) => set("years", e.target.value)} data-testid="verify-years" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[["veteran", "Veteran / retired"], ["transitioning", "Serving / transitioning"]].map(([v, label]) => (
                <button key={v} type="button" onClick={() => set("service_status", v)} data-testid={`verify-status-${v}`}
                  className={`px-3 py-2.5 text-sm border transition-colors ${f.service_status === v ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{label}</button>
              ))}
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer pt-1">
              <input type="checkbox" checked={f.attest} onChange={(e) => set("attest", e.target.checked)} data-testid="verify-attest" className="accent-gold mt-0.5" />
              I certify that I am a current or former member of the U.S. Armed Forces.
            </label>
            <button type="submit" disabled={busy} data-testid="verify-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Verify &amp; unlock my code
            </button>
            <p className="text-[11px] text-gray-500 text-center">Your code is personal and can't be shared publicly. Thank you for your service.</p>
          </form>
        )}
      </div>
    </div>
  );
}
